import { useEffect, useRef } from 'react';
import * as SignalR from '@microsoft/signalr';
import { useDesignerModeStore } from '@flow-studio/store';
import { useUIStore } from '@flow-studio/store';
import { useSettingsStore } from '@flow-studio/store';
import { apiConfig, processEngineExecutionApiClient } from '@flow-studio/api';
import { FlowObserverPanelEngine, useFlowObserverPanelStore } from '@flow-studio/flow-observer-core';
import type {
  ExecutionEventEnvelope,
} from '@flow-studio/types';
import type { ExecutionLogEntry, ExecutionStatusData } from '@flow-studio/flow-observer-core';
import { parseTopic } from '../utils/topicBuilder';
import { useHilContext } from '@bizfirst/hil-react';
import {
  extractHilPresentationInfo,
  normalizeHilEnvelope,
  patchEnvelopeResumeUrl,
} from '@bizfirst/hil-core';

let _nodeLogIdSeq = 0;
function nextNodeLogId(): string {
  return `node-log-${Date.now()}-${++_nodeLogIdSeq}`;
}

// ── SignalR event capture buffer ──────────────────────────────────────────────
// Collects every raw event so you can copy them from the browser console:
//   window.__copySignalRLogs()          → copies all events
//   window.__copySignalRLogs('ReceiveNodeActivity')  → copies one handler
//   window.__clearSignalRLogs()         → resets the buffer
interface SignalRRawEntry { handler: string; ts: string; payload: unknown }
const _signalRBuffer: SignalRRawEntry[] = [];
function _captureRaw(handler: string, payload: unknown): void {
  _signalRBuffer.push({ handler, ts: new Date().toISOString(), payload });
}
(window as any).__copySignalRLogs = (handler?: string) => {
  const entries = handler
    ? _signalRBuffer.filter((e) => e.handler === handler)
    : _signalRBuffer;
  const text = JSON.stringify(entries, null, 2);
  navigator.clipboard.writeText(text).then(
    () => console.log(`✅ Copied ${entries.length} SignalR events to clipboard`),
    () => { console.warn('Clipboard API unavailable — dumping here instead:'); console.log(text); }
  );
};
(window as any).__clearSignalRLogs = () => {
  _signalRBuffer.length = 0;
  console.log('🗑️ SignalR log buffer cleared');
};

let _skOutSeq = 0;
function emitSocketOutgoing(event: string, payload?: unknown): void {
  try {
    window.dispatchEvent(new CustomEvent('socket-log-outgoing', {
      detail: {
        _type: 'socket',
        id: `out-${Date.now()}-${++_skOutSeq}`,
        timestamp: new Date().toISOString(),
        direction: 'outgoing',
        event,
        topic: event,
        payload,
      },
    }));
  } catch { /* never break app */ }
}

/**
 * Imperatively join a node-specific SignalR stream group.
 * Not a hook — call this from components that need per-node log streaming.
 */
export function subscribeToNodeStream(
  connection: SignalR.HubConnection | null,
  executionResId: string,
  nodeKey: string,
  callback: (entry: ExecutionLogEntry) => void
): () => void {
  if (!connection || connection.state !== SignalR.HubConnectionState.Connected) {
    return () => {};
  }

  connection
    .invoke('JoinNodeStream', executionResId, nodeKey)
    .catch((err) => console.warn('[subscribeToNodeStream] Failed to join node stream:', err));

  const handler = (payload: any) => {
    const entry: ExecutionLogEntry = {
      id: nextNodeLogId(),
      timestamp: payload.timestamp ?? new Date().toISOString(),
      level: payload.level ?? 'info',
      sourceNodeId: nodeKey,
      sourceNodeLabel: payload.nodeLabel ?? nodeKey,
      message: payload.message ?? '',
      details: payload.details ?? undefined,
      eventType: 'node',
    };
    callback(entry);
  };

  connection.on('ReceiveNodeLog', handler);

  return () => {
    connection.off('ReceiveNodeLog', handler);
    if (connection.state === SignalR.HubConnectionState.Connected) {
      connection
        .invoke('LeaveNodeStream', executionResId, nodeKey)
        .catch((err) => console.warn('[subscribeToNodeStream] Failed to leave node stream:', err));
    }
  };
}

interface ConnectionStatus {
  isConnected: boolean;
  connectionState: SignalR.HubConnectionState;
  reconnectAttempts: number;
}

/**
 * Custom hook for real-time execution status updates via SignalR.
 * Replaces useExecutionPolling with WebSocket-based real-time updates.
 *
 * Usage:
 *   const { isConnected, connectionState } = useExecutionSignalR();
 */
export const useExecutionSignalR = () => {
  const mode = useDesignerModeStore((s) => s.mode);
  const currentExecutionId = useDesignerModeStore((s) => s.currentExecutionId);
  const updateNodeStatus = useDesignerModeStore((s) => s.updateNodeStatus);
  const completeExecution = useDesignerModeStore((s) => s.completeExecution);
  const updateExecutionStatus = useDesignerModeStore((s) => s.updateExecutionStatus);
  const setNodeInspectorData = useDesignerModeStore((s) => s.setNodeInspectorData);
  const showToast = useUIStore((s) => s.showToast);
  const { engine: hilEngine } = useHilContext();
  const enableHilTopLevelFallback = useSettingsStore((s) => s.settings.enableHilTopLevelFallback);

  const connectionRef = useRef<SignalR.HubConnection | null>(null);
  const connectionAttemptRef = useRef<boolean>(false);
  const currentExecutionIdRef = useRef(currentExecutionId);
  // Keep hilEngine in a ref so the SignalR closure always sees the latest instance
  const hilEngineRef = useRef(hilEngine);
  useEffect(() => { hilEngineRef.current = hilEngine; }, [hilEngine]);
  // Same rationale as hilEngineRef — connection.on() handlers are registered once inside
  // establishConnection() and would otherwise close over the flag's value at connect time,
  // never seeing a later toggle without a reconnect.
  const enableHilTopLevelFallbackRef = useRef(enableHilTopLevelFallback);
  useEffect(() => { enableHilTopLevelFallbackRef.current = enableHilTopLevelFallback; }, [enableHilTopLevelFallback]);
  const statusRef = useRef<ConnectionStatus>({
    isConnected: false,
    connectionState: SignalR.HubConnectionState.Disconnected,
    reconnectAttempts: 0,
  });

  // Keep ref in sync so onreconnected closure always sees the latest value
  useEffect(() => {
    currentExecutionIdRef.current = currentExecutionId;
  }, [currentExecutionId]);

  // Clean up connection on unmount or when execution ends
  useEffect(() => {
    return () => {
      if (connectionRef.current?.state === SignalR.HubConnectionState.Connected) {
        connectionRef.current
          .stop()
          .catch((err) => console.error('Error stopping SignalR connection:', err));
      }
    };
  }, []);

  // Main effect: manage connection lifecycle
  useEffect(() => {
    // Only connect in execution mode with active execution
    const shouldConnect = mode === 'execution' && currentExecutionId?.ResID;

    console.log('[SignalR] Effect running - checking conditions:', {
      mode,
      hasExecutionId: !!currentExecutionId,
      resID: currentExecutionId?.ResID?.substring(0, 8),
      shouldConnect,
    });

    if (!shouldConnect) {
      console.log('[SignalR] Conditions not met - skipping connection');
      connectionAttemptRef.current = false;
      // Disconnect if we're not in execution mode
      if (connectionRef.current?.state === SignalR.HubConnectionState.Connected) {
        console.log('🔌 Disconnecting SignalR (not in execution mode)');
        emitSocketOutgoing('LeaveExecution', { resID: currentExecutionId?.ResID || '' });
        connectionRef.current
          .invoke('LeaveExecution', currentExecutionId?.ResID || '')
          .catch((err) => console.error('Error leaving execution:', err));

        connectionRef.current
          .stop()
          .catch((err) => console.error('Error stopping connection:', err));
      }
      return;
    }

    console.log('[SignalR] Conditions met - establishing connection');

    // Build and start connection
    const establishConnection = async () => {
      // Prevent duplicate connection attempts (handles React StrictMode double-running effects)
      if (connectionAttemptRef.current || connectionRef.current?.state === SignalR.HubConnectionState.Connected) {
        console.log('[SignalR] Connection already in progress or established');
        return;
      }

      connectionAttemptRef.current = true;

      try {
        // Determine SignalR hub URL from backend API configuration
        // NOTE: Pass HTTP(S) URL to SignalR, it will negotiate and upgrade to WebSocket automatically
        const baseUrl = apiConfig.baseUrl;
        const hubUrl = `${baseUrl}/hubs/execution`;

        console.log(`🔗 Connecting to SignalR hub: ${hubUrl}`);

        const connection = new SignalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => {
              // Get auth token from localStorage or your auth store
              return localStorage.getItem('authToken') || '';
            },
            withCredentials: true,
          })
          .withAutomaticReconnect([0, 1000, 3000, 5000, 10000]) // Exponential backoff
          .withHubProtocol(new SignalR.JsonHubProtocol())
          .configureLogging(SignalR.LogLevel.Information)
          .build();

        connectionRef.current = connection;

        // ========== Register Client Handlers ==========

        /**
         * GENERIC HANDLER: ReceiveServerEvent with EdgeStream envelope routing
         *
         * Backend sends all ProcessEngine events via 'ReceiveServerEvent' with new envelope format:
         * - envelope = { protocol, messageId, meta, dataEnvelope: { data, metadata } }
         * - messageType.subType discriminates event type (ProcessStarted, NodeExecuted, etc.)
         *
         * This replaces 8 separate handlers with 1 generic handler that routes by messageType.subType
         */
        connection.on('ReceiveServerEvent', async (envelope: ExecutionEventEnvelope) => {
          _captureRaw('ReceiveServerEvent', envelope);
          console.log('[SignalR RAW] ReceiveServerEvent:', envelope);
          try {

            // Destructure envelope - EdgeStream v2 format: protocol + meta + dataEnvelope
            const { meta, dataEnvelope } = envelope;
            const { messageId, correlationId, topic } = meta;
            const { data, metadata, eventInfo } = dataEnvelope;
            const { messageType, sourceEntities } = metadata;
            const eventSubType = messageType?.subType; // Extract subType for routing

            // Log with correlation ID (first 8 chars of messageId)
            const logCorrelationId = messageId ? messageId.substring(0, 8) : 'unknown';
            const corrId = correlationId ? correlationId.substring(0, 8) : 'no-corr';
            console.debug(`[${logCorrelationId}] ${topic} (correlation: ${corrId})`);

            // Validate topic format (mirrors C# BuildTopic: process-engine.{entity}.{action})
            // Helps catch backend issues early
            const topicParts = parseTopic(topic);
            if (!topicParts) {
              console.warn(`⚠️ [SignalR] Invalid topic format: ${topic}`);
            }

            statusRef.current.isConnected = true;

            // Validate execution ID (security: prevent cross-execution message handling)
            if (data?.executionID !== currentExecutionId?.ResID) {
              console.warn(
                `⚠️ [SignalR] DROPPED ${eventSubType} — execution ID mismatch.`,
                `expected="${currentExecutionId?.ResID}", got="${data?.executionID}"`,
                envelope
              );
              return;
            }

            // Extract entity type from sourceEntities for proper routing
            const primaryEntity = sourceEntities?.[0];
            const entityType = primaryEntity?.type; // 'node', 'process-thread', 'process'

            // Route by messageType.subType
            switch (eventSubType) {
              case 'started': {
                // Route started events by entity type
                if (entityType === 'process') {
                  console.log(`📨 [SignalR] ProcessStarted:`, data);

                  // Dispatch to execution logs
                  const event = new CustomEvent('execution-event', {
                    detail: { messageId, type: 'ProcessStarted', data, eventInfo }
                  });
                  window.dispatchEvent(event);
                  break;
                }

                // Route WorkflowStarted (process-thread)
                if (entityType === 'process-thread') {
                  console.log(`📨 [SignalR] WorkflowStarted:`, data);
                  const event = new CustomEvent('execution-event', {
                    detail: { messageId, type: 'WorkflowStarted', data, eventInfo }
                  });
                  window.dispatchEvent(event);
                  break;
                }

                console.warn(`⚠️ [SignalR] 'started' event with unexpected entityType: ${entityType}`);
                break;
              }

              case 'executing': {
                // PublishThreadWaitingAsync/PublishThreadPausedAsync (HIL suspension / admin pause)
                // also raise eExecutionEventSubType.Executing at the ProcessThread/Process level --
                // the same subType 'node executing' uses. Route by entityType FIRST, same as the
                // 'started' case above, so a workflow-level Waiting/Paused event doesn't fall into
                // the node-only logic below and get treated as if node.id/key were a real node
                // (this previously went unnoticed because the backend's own routing switch silently
                // dropped these events entirely -- fixed 2026-09-24, which is what surfaced this
                // frontend gap; see ProcessEngineEventService.cs's ProcessThread+Executing case).
                if (entityType === 'process-thread' || entityType === 'process') {
                  const subStage = (data as any)?.subStage as string | undefined;
                  const isPaused = subStage === 'Paused';
                  const label = entityType === 'process' ? 'Process' : 'Workflow';
                  console.log(`📨 [SignalR] ${label}${isPaused ? 'Paused' : 'Waiting'}:`, data);
                  const waitingEvent = new CustomEvent('execution-event', {
                    detail: {
                      messageId,
                      type: entityType === 'process'
                        ? (isPaused ? 'ProcessPaused' : 'ProcessWaiting')
                        : (isPaused ? 'WorkflowPaused' : 'WorkflowWaiting'),
                      data,
                      eventInfo,
                    },
                  });
                  window.dispatchEvent(waitingEvent);
                  break;
                }

                console.log(`📨 [SignalR] NodeExecuting:`, data);

                // Dispatch to execution logs
                const event = new CustomEvent('execution-event', {
                  detail: { messageId, type: 'NodeExecuting', data, eventInfo }
                });
                window.dispatchEvent(event);

                // Use item.key (ProcessElementKey e.g. "node-1777029118371") when present —
                // it matches the React Flow node id. Fall back to integer id as string.
                const runningNodeId = data.item.key || data.item.id.toString();
                updateNodeStatus(runningNodeId, {
                  nodeId: runningNodeId,
                  status: 'running',
                  startedAt: data.status.startedAt || new Date().toISOString(),
                  ...(data.item.name ? { label: data.item.name } : {}),
                });
                break;
              }

              case 'progressing': {
                console.log(`📨 [SignalR] NodeProgressing:`, data);

                // Dispatch to execution logs
                const progressEvent = new CustomEvent('execution-event', {
                  detail: { messageId, type: 'NodeProgressing', data, eventInfo }
                });
                window.dispatchEvent(progressEvent);

                // Update node status based on the stage in the progress event
                const progressNodeId = data.item?.key || data.item?.id?.toString();
                if (progressNodeId) {
                  const stage = (data as any)?.stage || (eventInfo as any)?.eventDescription?.stage;

                  // Determine status based on completion stage
                  let progressStatus: 'running' | 'success' | 'failed' = 'running';
                  if (stage === 'Completed' || stage === 'Exit') {
                    progressStatus = 'success';
                  } else if (stage === 'Error') {
                    progressStatus = 'failed';
                  }

                  updateNodeStatus(progressNodeId, {
                    nodeId: progressNodeId,
                    status: progressStatus,
                    startedAt: data.status?.startedAt || new Date().toISOString(),
                    ...(data.item?.name ? { label: data.item.name } : {}),
                    ...(progressStatus !== 'running' && {
                      completedAt: data.status?.completedAt || new Date().toISOString(),
                      duration: data.status?.duration || 0,
                    }),
                  });

                  // Some node types (e.g. inside a Loop) only ever emit 'progressing'
                  // stage transitions and never a discrete 'completed' event — without
                  // this, such nodes show as "Success" in the node list but "No data"
                  // in the inspector, since only the 'completed' handler used to record
                  // inspector data. Mirror that logic here for terminal progress stages.
                  if (progressStatus !== 'running') {
                    const nodeActivity = (data as any).nodeActivity;
                    if (nodeActivity) {
                      setNodeInspectorData(progressNodeId, {
                        input: nodeActivity.inputData ?? undefined,
                        output: nodeActivity.outputData ?? undefined,
                        outputPortKey: nodeActivity.outputPortKey ?? data.status?.outputPortKey ?? undefined,
                        durationMs: data.status?.durationMs ?? undefined,
                        retryAttempt: nodeActivity.retryAttempt ?? undefined,
                        maxRetries: nodeActivity.maxRetries ?? undefined,
                        startedAt: data.status?.startedAt ?? undefined,
                        completedAt: data.status?.completedAt ?? undefined,
                        status: data.status?.name ?? (progressStatus === 'success' ? 'completed' : 'failed'),
                      });
                    } else {
                      const existing = useDesignerModeStore.getState().nodeInspectorData?.get(progressNodeId);
                      setNodeInspectorData(progressNodeId, {
                        ...existing,
                        outputPortKey: data.status?.outputPortKey ?? existing?.outputPortKey ?? undefined,
                        durationMs: data.status?.durationMs ?? existing?.durationMs ?? undefined,
                        startedAt: data.status?.startedAt ?? existing?.startedAt ?? undefined,
                        completedAt: data.status?.completedAt ?? new Date().toISOString(),
                        status: data.status?.name ?? (progressStatus === 'success' ? 'completed' : 'failed'),
                      });
                    }
                  }
                }

                // ── HIL suspension detection ─────────────────────────────────
                // When a HIL Form/Approval/Chat node suspends, the backend emits a
                // HIL-stage progress event with `hilInboxReferences` in the metadata.
                // The HilPresentationRequest is already embedded — push it directly
                // to the HIL engine without any extra REST calls.
                {
                  const hilInfo = extractHilPresentationInfo(data, eventInfo);
                  console.log('[SignalR][HIL] extractHilPresentationInfo result:', hilInfo);
                  if (hilInfo) {
                    const pushEnvelope = (rawEnvelope: any, inboxItemId?: number, source?: string) => {
                      // Backend sends camelCase; validator requires PascalCase — normalize first.
                      const normalized = normalizeHilEnvelope(rawEnvelope);
                      const patched = patchEnvelopeResumeUrl(normalized);
                      console.log(`[SignalR][HIL] pushing envelope to HIL engine (source=${source}):`, {
                        engageSessionID: hilInfo.engageSessionID ?? hilInfo.EngageSessionID,
                        inboxItemId,
                        hilType: patched.HilType,
                        formID: patched.Presentations?.[0]?.FormID,
                        session: patched.Session,
                        assignees: patched.Assignees,
                      });
                      hilEngineRef.current?.push(patched);
                    };

                    const refs: any[] = (hilInfo.InboxItemReferences ?? hilInfo.inboxItemReferences ?? []) as any[];
                    if (refs.length > 0) {
                      // Form/Approval path: one envelope per assigned actor's inbox item.
                      console.log(`[SignalR][HIL] ${refs.length} InboxItemReferences found — using per-actor path.`);
                      for (const ref of refs) {
                        const rawEnvelope = ref.HilPresentationRequest ?? ref.hilPresentationRequest;
                        if (!rawEnvelope) {
                          console.warn('[SignalR][HIL] InboxItemReference has no HilPresentationRequest, skipping:', ref);
                          continue;
                        }
                        const inboxItemId: number | undefined = ref.InboxItemID ?? ref.inboxItemID;
                        pushEnvelope(rawEnvelope, inboxItemId, 'inboxItemReferences');
                      }
                    } else if (enableHilTopLevelFallbackRef.current) {
                      // No per-actor inbox items (e.g. chat: Inbox disabled) — the envelope
                      // rides at hilInfo.HilPresentationRequest directly instead of inside
                      // InboxItemReferences[]. Gated behind enableHilTopLevelFallback
                      // (flow-studio-store settingsStore) so it can be switched off without a
                      // code change if it ever misbehaves.
                      const rawEnvelope = hilInfo.HilPresentationRequest ?? hilInfo.hilPresentationRequest;
                      if (rawEnvelope) {
                        console.log('[SignalR][HIL] No InboxItemReferences — using top-level HilPresentationRequest fallback.');
                        pushEnvelope(rawEnvelope, undefined, 'topLevelFallback');
                      } else {
                        console.warn('[SignalR][HIL] No InboxItemReferences and no top-level HilPresentationRequest — nothing to push.', hilInfo);
                      }
                    } else {
                      console.warn('[SignalR][HIL] No InboxItemReferences, and enableHilTopLevelFallback is OFF — envelope dropped. Toggle it back on via useSettingsStore.getState().updateSettings({ enableHilTopLevelFallback: true }).', hilInfo);
                    }
                  }
                }
                break;
              }

              case 'completed': {
                // Route completed events by entity type
                if (entityType === 'node') {
                  console.log(`📨 [SignalR] NodeExecuted:`, data);

                  // Dispatch to execution logs
                  const event = new CustomEvent('execution-event', {
                    detail: { messageId, type: 'NodeExecuted', data, eventInfo }
                  });
                  window.dispatchEvent(event);

                  // Use item.key (ProcessElementKey) when present — matches React Flow node id.
                  const completedNodeId = data.item.key || data.item.id.toString();
                  console.log(`[SignalR] Marking node as completed:`, {
                    nodeKey: data.item.key,
                    nodeId: data.item.id,
                    completedNodeId,
                    itemName: data.item.name
                  });
                  updateNodeStatus(completedNodeId, {
                    nodeId: completedNodeId,
                    status: 'success',
                    completedAt: data.status.completedAt || new Date().toISOString(),
                    duration: data.status.duration || 0,
                    error: data.error?.errorMessage ? { message: data.error.errorMessage } : undefined,
                    ...(data.item.name ? { label: data.item.name } : {}),
                  });

                  // Store node inspector data from nodeActivity if present
                  const nodeActivity = (data as any).nodeActivity;
                  if (nodeActivity) {
                    setNodeInspectorData(completedNodeId, {
                      input: nodeActivity.inputData ?? undefined,
                      output: nodeActivity.outputData ?? undefined,
                      outputPortKey: nodeActivity.outputPortKey ?? data.status?.outputPortKey ?? undefined,
                      durationMs: data.status?.durationMs ?? undefined,
                      retryAttempt: nodeActivity.retryAttempt ?? undefined,
                      maxRetries: nodeActivity.maxRetries ?? undefined,
                      startedAt: data.status?.startedAt ?? undefined,
                      completedAt: data.status?.completedAt ?? undefined,
                      status: data.status?.name ?? 'completed',
                    });
                  } else {
                    const existing = useDesignerModeStore.getState().nodeInspectorData?.get(completedNodeId);
                    setNodeInspectorData(completedNodeId, {
                      ...existing,
                      outputPortKey: data.status?.outputPortKey ?? existing?.outputPortKey ?? undefined,
                      durationMs: data.status?.durationMs ?? existing?.durationMs ?? undefined,
                      startedAt: data.status?.startedAt ?? existing?.startedAt ?? undefined,
                      completedAt: data.status?.completedAt ?? new Date().toISOString(),
                      status: data.status?.name ?? 'completed',
                    });
                  }
                  break;
                }

                if (entityType === 'process-thread') {
                  console.log(`📨 [SignalR] WorkflowCompleted:`, data);

                  // Dispatch to execution logs
                  const event = new CustomEvent('execution-event', {
                    detail: { messageId, type: 'WorkflowCompleted', data, eventInfo }
                  });
                  window.dispatchEvent(event);
                  break;
                }

                if (entityType === 'process') {
                  console.log(`📨 [SignalR] ProcessCompleted:`, data);

                  // Dispatch to execution logs
                  const event = new CustomEvent('execution-event', {
                    detail: { messageId, type: 'ProcessCompleted', data, eventInfo }
                  });
                  window.dispatchEvent(event);

                  // Map backend status to frontend state
                  const statusMap: Record<string, any> = {
                    'Completed': 'completed',
                    'Failed': 'failed',
                    'Cancelled': 'cancelled',
                    'Errored': 'failed',
                    'Started': 'running',
                  };

                  const executionState = statusMap[data.status.name] || 'completed';

                  // Fetch final node counts before completing — polling stops when we call
                  // completeExecution() so it won't get one last authoritative read on its own.
                  // baseApiClient strips GoWebResponse wrapper and converts keys to PascalCase,
                  // so finalResponse.data IS the ExecutionStatusData object with PascalCase keys.
                  try {
                    const finalResponse = await processEngineExecutionApiClient.getExecutionStatus(
                      currentExecutionId!
                    );
                    if (finalResponse.success && finalResponse.data) {
                      const execStatus = finalResponse.data as any;
                      updateExecutionStatus({
                        processExecutionID: execStatus.ProcessExecutionID || 0,
                        processID: execStatus.ProcessID || 0,
                        statusName: data.status.name || 'Completed',
                        isActive: false,
                        progress: {
                          totalNodes: execStatus.Progress?.TotalNodes || 0,
                          completedNodes: execStatus.Progress?.CompletedNodes || 0,
                          failedNodes: execStatus.Progress?.FailedNodes || 0,
                          skippedNodes: execStatus.Progress?.SkippedNodes || 0,
                          percentage: execStatus.Progress?.Percentage || 0,
                        },
                        timing: {
                          startedAt: execStatus.Timing?.StartedAt,
                          stoppedAt: execStatus.Timing?.StoppedAt,
                          durationMs: execStatus.Timing?.DurationMs,
                        },
                        threadExecutionInfoList: (execStatus.ThreadExecutionInfoList || []).map((t: any) => ({
                          threadExecutionID: t.ThreadExecutionID,
                          processThreadID: t.ProcessThreadID,
                          status: t.Status,
                          completedNodes: t.CompletedNodes,
                          totalNodes: t.TotalNodes,
                        })),
                        errorInfo: execStatus.ErrorInfo ? {
                          message: execStatus.ErrorInfo.Message || '',
                          stackTrace: execStatus.ErrorInfo.StackTrace,
                          nodeID: execStatus.ErrorInfo.NodeID,
                        } : undefined,
                      });
                    }
                  } catch (finalFetchErr) {
                    console.warn('[SignalR] Could not fetch final node counts:', finalFetchErr);
                  }

                  // Mark execution as complete
                  completeExecution(executionState);

                  // Disconnect after execution completes
                  console.log('🔌 Disconnecting after process completion');
                  connection.stop().catch((err) => {
                    console.error('Error stopping connection:', err);
                  });
                  break;
                }
              }

              case 'errored': {
                console.log(`📨 [SignalR] ExecutionError:`, data);

                // Dispatch to execution logs
                const event = new CustomEvent('execution-event', {
                  detail: { messageId, type: 'ExecutionError', data, eventInfo }
                });
                window.dispatchEvent(event);

                // If error occurred at a node, mark it as failed
                if (data.item?.id) {
                  const failedNodeId = data.item.key || data.item.id.toString();
                  updateNodeStatus(failedNodeId, {
                    nodeId: failedNodeId,
                    status: 'failed',
                    error: {
                      message: data.error?.errorMessage || data.message,
                      stackTrace: data.error?.stackTrace
                    },
                    ...(data.item?.name ? { label: data.item.name } : {}),
                  });
                }
                break;
              }

              case 'test-message': {
                console.log(`📨 [SignalR] TestMessage:`, data);
                showToast('success', `💬 Test message received: ${data.message}`);

                // Dispatch event so ChatPanel can receive it
                const testEvent = new CustomEvent('signalr-test-message', { detail: data });
                window.dispatchEvent(testEvent);
                break;
              }

              default: {
                console.warn(`⚠️ [SignalR] Unknown eventSubType: ${eventSubType}`);
              }
            }
          } catch (error) {
            console.error('❌ [SignalR] Error processing ReceiveServerEvent:', error);
            showToast('error', 'Error processing server event');
          }
        });

        /**
         * Handler: SignalR Errors
         */
        connection.on('ReceiveError', (message: string) => {
          console.error('📨 [SignalR] ReceiveError:', message);
          showToast('error', `SignalR error: ${message}`);
        });

        /**
         * Handler: Connection Status Changes
         */
        connection.onreconnecting((error) => {
          console.warn('SignalR attempting to reconnect:', error);
          statusRef.current.reconnectAttempts++;
          showToast('warning', `Reconnecting to execution service (attempt ${statusRef.current.reconnectAttempts})...`);
        });

        connection.onreconnected(async (_connectionId) => {
          statusRef.current.reconnectAttempts = 0;
          statusRef.current.isConnected = true;
          showToast('success', 'Reconnected to execution service');

          // Hub group membership is wiped on reconnect — re-join to resume live events
          const resID = currentExecutionIdRef.current?.ResID;
          if (resID) {
            try {
              emitSocketOutgoing('reconnect+JoinExecution', { resID });
              await connection.invoke('JoinExecution', resID);
              console.log('[SignalR] Re-joined execution group after reconnect:', resID);
            } catch (err) {
              console.warn('[SignalR] Failed to re-join execution group after reconnect:', err);
            }
          }
        });

        connection.onclose((_error) => {
          statusRef.current.isConnected = false;
        });

        /**
         * Handler: SignalR Errors
         */
        connection.on('ReceiveError', (message: string) => {
          console.error('📨 [SignalR] ReceiveError:', message);
          showToast('error', `SignalR error: ${message}`);
        });

        /**
         * Handler: Test Message (for Chat Panel testing)
         */
        connection.on('ReceiveTestMessage', (data: any) => {
          console.log('📨 [SignalR] ReceiveTestMessage:', data);
          showToast('success', `Test message received: ${data.message}`);

          // Dispatch event so ChatPanel can receive it
          const event = new CustomEvent('signalr-test-message', { detail: data });
          window.dispatchEvent(event);
        });

        /**
         * Handler: ReceiveNodeActivity — full node activity event emitted after each node completes.
         * Envelope format: { protocol, meta, dataEnvelope: { data: NodeActivityData, metadata } }
         * NodeActivityData.logs contains every LogActivity() call made during that node's execution.
         * This is the primary source of detailed per-node logs in the Execution Logs tab.
         */
        connection.on('ReceiveNodeActivity', (envelope: any) => {
          _captureRaw('ReceiveNodeActivity', envelope);
          console.log('[SignalR RAW] ReceiveNodeActivity:', envelope);
          try {
            const data = envelope?.dataEnvelope?.data ?? envelope?.data ?? envelope;
            if (!data) return;

            const nodeKey: string = data.nodeKey ?? data.NodeKey ?? '';
            const nodeLabel: string = data.nodeLabel ?? data.NodeLabel ?? nodeKey;
            const logs: any[] = data.logs ?? data.Logs ?? [];

            for (const log of logs) {
              const entry: ExecutionLogEntry = {
                id: nextNodeLogId(),
                timestamp: log.timestamp ?? log.Timestamp ?? new Date().toISOString(),
                level: (log.level ?? log.Level ?? 'info') as ExecutionLogEntry['level'],
                sourceNodeId: nodeKey || undefined,
                sourceNodeLabel: nodeLabel || undefined,
                message: log.message ?? log.Message ?? '',
                details: log.data ?? log.Data
                  ? JSON.stringify(log.data ?? log.Data)
                  : undefined,
                eventType: 'node',
              };
              FlowObserverPanelEngine.pushData('execution-logs', entry);
            }

            // Update node inspector with full activity data (input/output/duration/status)
            if (nodeKey) {
              setNodeInspectorData(nodeKey, {
                input: data.inputData ?? data.InputData ?? undefined,
                output: data.outputData ?? data.OutputData ?? undefined,
                outputPortKey: data.outputPortKey ?? data.OutputPortKey ?? undefined,
                durationMs: data.durationMs ?? data.DurationMs ?? undefined,
                retryAttempt: data.retryAttempt ?? data.RetryAttempt ?? undefined,
                startedAt: data.startedAt ?? data.StartedAt ?? undefined,
                completedAt: data.completedAt ?? data.CompletedAt ?? undefined,
                status: data.status ?? data.Status ?? 'completed',
              });
            }

            console.debug(`[SignalR] ReceiveNodeActivity: node=${nodeLabel} logs=${logs.length}`);
          } catch (err) {
            console.warn('[SignalR] Error processing ReceiveNodeActivity:', err);
          }
        });

        /**
         * Handler: Group Join Confirmation (server sends this after GenericJoinGroup)
         */
        connection.on('receivegroupjoinedconfirm', (groupName: string) => {
          console.log('[SignalR] Group join confirmed:', groupName);
        });

        /**
         * Handler: ReceiveHilEvent — HIL suspension events (APPROVAL, FORM, CHAT, APPROVAL_RESOLVED …)
         * Broadcast by WorkflowSignalREventProducer to the execution group AND the hil-stream group.
         *
         * The handler is registered here (alongside all other connection.on calls, before
         * connection.start()) rather than in useHilSignalR, because useHilSignalR receives the
         * connection through React's render cycle. The connection starts as a ref (null at first
         * render) and React does not re-render when the ref becomes non-null, so the handler in
         * useHilSignalR would never fire. Dispatching a window CustomEvent follows the same pattern
         * already used by ReceiveServerEvent → 'execution-event'.
         */
        connection.on('ReceiveHilEvent', (envelope: any) => {
          _captureRaw('ReceiveHilEvent', envelope);
          console.log('[SignalR RAW] ReceiveHilEvent:', envelope);
          try {
            const hilData = envelope?.dataEnvelope?.data ?? envelope?.DataEnvelope?.Data ?? envelope;
            if (hilData && hilEngineRef.current) {
              const normalized = normalizeHilEnvelope(hilData);
              console.log('[SignalR][HIL] ReceiveHilEvent — pushing directly to HIL engine:', {
                hilType: normalized.HilType,
                engageSessionID: normalized.Session?.EngageSessionID,
              });
              hilEngineRef.current.push(normalized);
            } else {
              console.log('[SignalR][HIL] ReceiveHilEvent — no hilEngine/hilData yet, falling back to window "hil-event" dispatch.');
              // Fallback: dispatch window event for any other listener. Must normalize here too
              // (same as the direct-push branch above) — useHilSignalR's handler pushes
              // payload?.dataEnvelope?.data ?? payload straight into HilEnvelopeValidator with no
              // normalization step of its own. Without this, a real chat/form HIL envelope
              // arriving before hilEngineRef is set was silently rejected by the validator
              // (PascalCase-only) since the backend sends camelCase — the chat window (and any
              // other HIL surface) would never appear, with no visible error beyond a console
              // "Envelope REJECTED by validator" line.
              const fallbackDetail = hilData ? normalizeHilEnvelope(hilData) : envelope;
              window.dispatchEvent(new CustomEvent('hil-event', { detail: fallbackDetail }));
            }
          } catch (err) {
            console.warn('[SignalR] Error handling ReceiveHilEvent:', err);
          }
        });

        // Log all registered handlers
        console.log('✅ [SignalR] All message handlers registered:');
        console.log('  - ReceiveServerEvent (with eventSubType routing):');
        console.log('    • ProcessStarted');
        console.log('    • ProcessCompleted');
        console.log('    • WorkflowStarted');
        console.log('    • WorkflowCompleted');
        console.log('    • NodeExecuting');
        console.log('    • NodeExecuted');
        console.log('    • ExecutionError');
        console.log('    • TestMessage');
        console.log('  - ReceiveError');

        // ========== Start Connection ==========
        console.log('🔗 [SignalR] Connecting to execution hub...');
        await connection.start();
        emitSocketOutgoing('connect', { hubUrl });
        console.log(' [SignalR] Connected to execution hub');

        statusRef.current.isConnected = true;
        statusRef.current.connectionState = connection.state;

        // Join the execution group using ResID (GUID)
        if (!currentExecutionId?.ResID) {
          throw new Error('Cannot join execution: missing execution ResID');
        }

        try {
          console.log('🔗 [SignalR] CALLING JoinExecution with ResID:', currentExecutionId.ResID);
          emitSocketOutgoing('JoinExecution', { resID: currentExecutionId.ResID });
          await connection.invoke('JoinExecution', currentExecutionId.ResID);
          console.log(' [SignalR] Successfully joined execution group:', currentExecutionId.ResID);

          // Unblocks TopToolbar's join-before-execute wait: the toolbar sets currentExecutionId
          // (via startExecution) and waits for this flag BEFORE firing the /execute POST, so the
          // backend can't finish broadcasting live events before this client is in the group.
          useDesignerModeStore.getState().setExecutionGroupJoined(true);

          // Also join the HIL stream group so the ReceiveHilEvent handler fires for
          // HIL-only subscribers (non-execution-group watchers). Non-critical — execution
          // group already delivers ReceiveHilEvent, but this ensures completeness.
          emitSocketOutgoing('JoinHilStream', { resID: currentExecutionId.ResID });
          connection.invoke('JoinHilStream', currentExecutionId.ResID)
            .then(() => console.log('[SignalR] Joined HIL stream group:', currentExecutionId.ResID))
            .catch((err) => console.warn('[SignalR] Failed to join HIL stream group (non-critical):', err));

          // Catch-up: fetch node activity logs from DB to populate nodeInspectorData for
          // nodes whose ReceiveNodeActivity events fired before this client joined the group.
          // This is always attempted (not gated on buffer state) — it's the only reliable
          // way to show per-node output when the workflow completes faster than the SignalR handshake.
          // Per-node log lines rebuilt from history, for nodes whose live events were missed.
          // Pushed to the 'execution-logs' tab in the status catch-up below.
          const catchUpNodeLogs: ExecutionLogEntry[] = [];
          try {
            const nodeLogsResponse = await processEngineExecutionApiClient.getNodeActivityLogsByExecutionResId(
              currentExecutionId.ResID
            );
            if (nodeLogsResponse.success && nodeLogsResponse.data) {
              const rawData = nodeLogsResponse.data as any;
              const items: any[] = Array.isArray(rawData)
                ? rawData
                : (rawData.Items ?? rawData.items ?? rawData.Data ?? rawData.data ?? []);
              console.log('[SignalR] Node activity catch-up: items from history =', items.length, items[0] ? Object.keys(items[0]) : []);
              if (items.length > 0) {
                const currentMap = useDesignerModeStore.getState().nodeInspectorData;
                let populated = 0;
                for (const item of items) {
                  const nodeKey: string = item.NodeKey ?? item.nodeKey ?? '';
                  if (!nodeKey) continue;
                  // Log lines are built for every node (independent of the inspector-map check
                  // below): the map is keyed by nodeKey and can still hold a previous run's data,
                  // which would otherwise suppress the log. They are only pushed when the log
                  // buffer is empty, i.e. no live events were received for this run.
                  const nodeStatus: string = String(item.Status ?? item.status ?? 'completed');
                  const nodeLabel: string =
                    item.NodeName ?? item.nodeName ?? item.NodeLabel ?? item.nodeLabel ?? item.ElementName ?? item.elementName ?? nodeKey;
                  const nodeError: string | undefined = item.ErrorMessage ?? item.errorMessage ?? item.Error ?? item.error ?? undefined;
                  const nodeDurationMs: number | undefined = item.DurationMs ?? item.durationMs ?? undefined;
                  const nodeFailed = /fail|error/i.test(nodeStatus);
                  catchUpNodeLogs.push({
                    id: `catchup-node-${nodeKey}-${catchUpNodeLogs.length}`,
                    timestamp: item.CompletedAt ?? item.completedAt ?? item.StartedAt ?? item.startedAt ?? new Date().toISOString(),
                    level: nodeFailed ? 'error' : 'info',
                    sourceNodeId: nodeKey,
                    sourceNodeLabel: nodeLabel,
                    message: `${nodeLabel} ${nodeStatus.toLowerCase()}${nodeDurationMs != null ? ` (${nodeDurationMs} ms)` : ''}${nodeError ? ` — ${nodeError}` : ''}`,
                    eventType: 'node',
                    status: nodeStatus,
                    durationMs: nodeDurationMs,
                  });
                  // Skip nodes that already received a live ReceiveNodeActivity event
                  if (currentMap?.has(nodeKey)) continue;
                  let outputData: any;
                  let inputData: any;
                  try {
                    const outJson = item.OutputDataJson ?? item.outputDataJson;
                    if (outJson) outputData = JSON.parse(outJson);
                  } catch { /* ignore malformed JSON */ }
                  try {
                    const inJson = item.InputDataJson ?? item.inputDataJson;
                    if (inJson) inputData = JSON.parse(inJson);
                  } catch { /* ignore malformed JSON */ }
                  setNodeInspectorData(nodeKey, {
                    input: inputData,
                    output: outputData,
                    outputPortKey: item.OutputPortKey ?? item.outputPortKey ?? undefined,
                    durationMs: item.DurationMs ?? item.durationMs ?? undefined,
                    retryAttempt: item.RetryAttempt ?? item.retryAttempt ?? undefined,
                    startedAt: item.StartedAt ?? item.startedAt ?? undefined,
                    completedAt: item.CompletedAt ?? item.completedAt ?? undefined,
                    status: item.Status ?? item.status ?? 'completed',
                  });
                  populated++;
                }
                console.log(`[SignalR] Node activity catch-up: populated ${populated}/${items.length} nodes from history`);
              }
            }
          } catch (nodeActivityErr) {
            console.warn('[SignalR] Could not fetch node activity logs for catch-up:', nodeActivityErr);
          }

          // Catch-up: if execution events (WorkflowStarted, NodeExecuting…) were broadcast
          // before this client joined the group (race condition on new execution, or remount
          // during an active execution), the observer panel buffers will be empty.
          // Fetch current status from the API and seed the panels so the user sees live state.
          const tabState = useFlowObserverPanelStore.getState().tabRuntimeStates['execution-status'];
          if (!tabState?.dataBuffer?.length) {
            try {
              const statusResponse = await processEngineExecutionApiClient.getExecutionStatus(currentExecutionId!);
              if (statusResponse.success && statusResponse.data) {
                const rawData = statusResponse.data as any;
                // Backend may nest execution fields under an 'Execution' key or return them flat
                const exec = rawData.Execution ?? rawData.execution ?? rawData;
                const isActive: boolean = exec.IsActive ?? exec.isActive ?? false;
                const statusName: string = exec.StatusName ?? exec.statusName ?? 'Completed';
                const progress = exec.Progress ?? exec.progress ?? {};
                const timing = exec.Timing ?? exec.timing ?? {};
                const startedAt: string | undefined = timing.StartedAt ?? timing.startedAt ?? undefined;
                const stoppedAt: string | undefined = timing.StoppedAt ?? timing.stoppedAt ?? undefined;

                FlowObserverPanelEngine.setTabEngineState('execution-status', 'available');
                FlowObserverPanelEngine.setTabEngineState('execution-logs', 'available');
                // §5.1 gate: only force-focus these tabs if the user hasn't already
                // picked one this session (e.g. Dashboard) — same as the WorkflowStarted
                // handler in useFlowObserverSignalR.ts, and same fix: land on 'dashboard'
                // last so it wins focus, matching TopToolbar.handleExecute's default.
                // Without this, this catch-up path (reconnect / remount during an active
                // execution) re-triggers the same nav-jump the WorkflowStarted fix prevents.
                if (!FlowObserverPanelEngine.hasUserSelectedTab()) {
                  FlowObserverPanelEngine.activateTab('execution-logs');
                  FlowObserverPanelEngine.activateTab('execution-status');
                  FlowObserverPanelEngine.activateTab('dashboard');
                }

                const catchUpStatus: ExecutionStatusData = {
                  runState: isActive ? 'running' : 'completed',
                  nodesTotal: progress.TotalNodes ?? progress.totalNodes ?? 0,
                  nodesCompleted: progress.CompletedNodes ?? progress.completedNodes ?? 0,
                  nodesFailed: progress.FailedNodes ?? progress.failedNodes ?? 0,
                  startedAt,
                  elapsedMs: startedAt
                    ? Date.now() - new Date(/[Zz]$|[+-]\d{2}:?\d{2}$/.test(startedAt) ? startedAt : startedAt + 'Z').getTime()
                    : 0,
                  ...(stoppedAt ? { completedAt: stoppedAt } : {}),
                };
                FlowObserverPanelEngine.pushData('execution-status', catchUpStatus);

                catchUpNodeLogs
                  .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
                  .forEach((entry) => FlowObserverPanelEngine.pushData('execution-logs', entry));

                const catchUpLog: ExecutionLogEntry = {
                  id: `catchup-${Date.now()}`,
                  timestamp: new Date().toISOString(),
                  level: 'info',
                  message: isActive
                    ? 'Execution in progress — connected to live updates'
                    : `Execution ${statusName.toLowerCase()} — results loaded from history`,
                };
                FlowObserverPanelEngine.pushData('execution-logs', catchUpLog);

                if (isActive) {
                  // Kick the FlowObserver elapsed timer with the server's UTC start time so
                  // subsequent NodeExecuting events compute elapsedMs from the correct origin.
                  if (startedAt) {
                    const catchupEvent = new CustomEvent('execution-event', {
                      detail: { messageId: 'catchup', type: 'ExecutionCatchup', data: { startedAt } },
                    });
                    window.dispatchEvent(catchupEvent);
                  }
                  console.log('[SignalR] Catch-up: execution still active, listening for live events');
                } else {
                  // Execution already finished — update store state and disconnect
                  const stateMap: Record<string, any> = {
                    Completed: 'completed', CompletedWithWarnings: 'completedWithWarnings',
                    Failed: 'failed', Cancelled: 'cancelled', TimedOut: 'timedOut',
                  };
                  completeExecution(stateMap[statusName] ?? 'completed');
                  console.log('[SignalR] Catch-up: execution already finished, disconnecting');
                  connection.stop().catch((err) => console.warn('[SignalR] Stop after catch-up:', err));
                }
              }
            } catch (catchUpErr) {
              console.warn('[SignalR] Could not fetch catch-up status:', catchUpErr);
            }
          }
        } catch (joinError) {
          const errorMsg = joinError instanceof Error ? joinError.message : String(joinError);
          console.error(' Failed to join execution group:', errorMsg);
          showToast('error', `Failed to join execution updates: ${errorMsg}`);
          statusRef.current.isConnected = false;
          throw joinError;
        }
      } catch (error) {
        console.error(' Failed to establish SignalR connection:', error);
        statusRef.current.isConnected = false;
        showToast(
          'error',
          `Failed to connect to real-time updates: ${error instanceof Error ? error.message : 'Unknown error'}`
        );
      } finally {
        connectionAttemptRef.current = false;
      }
    };

    establishConnection();

    // Cleanup: Leave group when mode changes or execution ID changes
    return () => {
      const resID = currentExecutionId?.ResID;
      if (resID && connectionRef.current?.state === SignalR.HubConnectionState.Connected) {
        emitSocketOutgoing('LeaveExecution', { resID });
        connectionRef.current
          .invoke('LeaveExecution', resID)
          .catch((err) => {
            const errorMsg = err instanceof Error ? err.message : String(err);
            console.warn('[SignalR] Error leaving execution group:', errorMsg);
          });
      }
    };
  }, [mode, currentExecutionId?.ResID]);

  // Return connection status
  return {
    isConnected: statusRef.current.isConnected,
    connectionState: connectionRef.current?.state ?? SignalR.HubConnectionState.Disconnected,
    reconnectAttempts: statusRef.current.reconnectAttempts,
    connection: connectionRef.current,
  };
};
